import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  inject,
} from '@angular/core';

import { ToastService } from '../../../core/services/toast.service';

/**
 * The corner stack of failure notices fired by {@link errorInterceptor}, pinned below the sticky
 * portal header at the top-end of the viewport and painted above every dialog in the app.
 *
 * <p>Deliberately *not* SweetAlert2: Swal has one global instance, so a toast fired while a dialog
 * is open would replace it rather than sit beside it. See {@link ToastService} for the two defects
 * that came out of that, and `toast-host.scss` for the layering and the header offset.
 *
 * <p><b>Announcement.</b> The container is a permanent live region (`role="status"`,
 * `aria-live="polite"`) so it already exists in the accessibility tree when the first toast lands —
 * a region created at the same moment as its content is announced unreliably. It is explicitly
 * `aria-atomic="false"`: `role="status"` carries an implicit `aria-atomic="true"`, which would make
 * every arrival, expiry and dismissal re-read the *whole* stack, so a flapping backend would have a
 * screen reader reciting the same two paragraphs every few seconds. Each toast then declares its
 * own urgency and its own atomicity inside it — read as a whole sentence, but only the toast that
 * changed. A read that failed is polite (the user lost nothing and can finish the sentence they
 * were reading), a write that failed is `role="alert"` / `aria-live="assertive"` and interrupts,
 * because "your data was not saved" cannot wait for a pause in the screen reader's queue.
 *
 * <p><b>Why the host element moves to `document.body`.</b> Every modal SweetAlert2 sets
 * `aria-hidden="true"` on each `document.body` child that does not contain the Swal container —
 * `<app-root>` included. Left inside the shell, a toast raised while any dialog is open would be
 * visible but silently absent from the accessibility tree, and live regions do not replay content
 * that was already there when the dialog closes, so the failure would never be announced at all.
 * That is not hypothetical: several pages fire `Swal.fire(...)` without awaiting it and then start
 * the next request straight away. Hoisting the host out of `<app-root>` puts it in Swal's sweep
 * instead of under it, and the observer below drops the attribute Swal writes on it. The page
 * behind the dialog stays hidden, as it should — this element is not behind the dialog, it renders
 * above it.
 *
 * <p>Only the DOM node moves; the component still lives above the router outlet in `App`, so it
 * survives every route change exactly as before.
 */
@Component({
  selector: 'app-toast-host',
  template: `
    <div class="toast-host" role="status" aria-live="polite" aria-atomic="false">
      @for (toast of toasts.toasts(); track toast.id) {
        <div
          class="toast-host__item"
          [class.toast-host__item--persistent]="toast.persistent"
          [attr.role]="toast.persistent ? 'alert' : 'status'"
          [attr.aria-live]="toast.persistent ? 'assertive' : 'polite'"
          aria-atomic="true"
        >
          <i class="bi bi-exclamation-triangle-fill toast-host__icon" aria-hidden="true"></i>
          <div class="toast-host__body">
            <p class="toast-host__title">{{ toast.title }}</p>
            <p class="toast-host__text">{{ toast.text }}</p>
          </div>
          <button
            type="button"
            class="toast-host__close"
            aria-label="ปิด"
            (click)="toasts.dismiss(toast.id)"
          >
            <i class="bi bi-x-lg" aria-hidden="true"></i>
          </button>
        </div>
      }
    </div>
  `,
  styleUrl: './toast-host.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ToastHost implements OnInit, OnDestroy {
  protected readonly toasts = inject(ToastService);

  private readonly host: HTMLElement = inject(ElementRef).nativeElement;
  private observer?: MutationObserver;

  ngOnInit(): void {
    document.body.appendChild(this.host);

    // SweetAlert2 re-applies `aria-hidden="true"` on every modal open and never asks first, so a
    // one-off removal would not hold. Removing an absent attribute is a no-op and records no
    // mutation, so this settles rather than looping.
    this.observer = new MutationObserver(() => this.host.removeAttribute('aria-hidden'));
    this.observer.observe(this.host, { attributes: true, attributeFilter: ['aria-hidden'] });
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
    // Angular's renderer removes the host by `element.remove()` and ignores the recorded parent,
    // so the move above is safe; this only covers the component being torn down on its own.
    this.host.remove();
  }
}
